import React from 'react';

import { css, cx } from '@emotion/css';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles.ts';
import { useWindowSize } from '@/hooks/useWindowSize.ts';
import { CarePlans } from '@/models/CarePlanModel.ts';
import {
  Condition,
  diabetesTypeTranslationMapping,
} from '@/models/ConditionModel.ts';
import { Patient } from '@/models/PatientModel.ts';
import { userFormattedName } from '@/models/UserModel.ts';
import { Queries } from '@/queries/Queries.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { ScreenMax, ScreenSizes } from '@/theme/responsive.ts';
import { Age } from '@/uiKit/atoms/Age';
import { Tooltip } from '@/uiKit/atoms/floating/Tooltip.tsx';
import { TelemonitoringActivityTag } from '@/uiKit/molecules/TelemonitoringActivityTag.tsx';

export type PatientHeaderInfoProps = {
  patient: Patient;
  className?: string;
};

export const PatientHeaderInfo: React.FC<PatientHeaderInfoProps> = ({
  patient,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const condition = Queries.condition.useCondition(patient?.id);
  const carePlans = Queries.practitioner.useCarePlans(patient.id);
  const { width } = useWindowSize();

  return (
    <div className={cx(styles.container, className, 'scrollbar')}>
      <Tooltip
        placement="bottom-start"
        content={
          <PatientHeaderInfoMobile
            patient={patient}
            condition={condition}
            carePlans={carePlans}
          />
        }
        disabled={!width || width > ScreenSizes.MD}
      >
        <div className={styles.infos}>
          <Typography variant="subtitleLarge" className={styles.text}>
            {userFormattedName(patient.familyName, patient.givenName)}
          </Typography>
          <Age birthDate={patient.birthDate} className={styles.text} />
          <Typography variant="body" className={cx(styles.text)}>
            {t(
              diabetesTypeTranslationMapping(
                condition.type,
                condition.medicationSchema,
              ),
            )}
          </Typography>
          {condition.termDate && (
            <Typography variant="body">
              {`${t('condition.term')} : `}
              {DateTime.fromISO(condition.termDate).toLocaleString(
                DateTime.DATE_SHORT,
              )}
            </Typography>
          )}
          <TelemonitoringActivityTag
            className={styles.tag}
            tags={carePlans.telemonitoring_tags}
            format="long"
          />
        </div>
      </Tooltip>
    </div>
  );
};

type PatientHeaderInfoMobileProps = {
  patient: Patient;
  condition: Condition;
  carePlans: CarePlans;
  className?: string;
};

const PatientHeaderInfoMobile: React.FC<PatientHeaderInfoMobileProps> = ({
  patient,
  condition,
  carePlans,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={cx(styles.mobileContainer, className)}>
      <Typography variant="subtitleLarge" className={styles.text}>
        {userFormattedName(patient.familyName, patient.givenName)}
      </Typography>
      <Age birthDate={patient.birthDate} className={styles.text} />
      <Typography variant="body" className={cx(styles.text)}>
        {t(
          diabetesTypeTranslationMapping(
            condition.type,
            condition.medicationSchema,
          ),
        )}
      </Typography>
      {condition.termDate && (
        <Typography variant="body">
          {`${t('condition.term')} : `}
          {DateTime.fromISO(condition.termDate).toLocaleString(
            DateTime.DATE_SHORT,
          )}
        </Typography>
      )}
      <TelemonitoringActivityTag
        className={styles.tag}
        tags={carePlans.telemonitoring_tags}
        format="long"
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex: 1 1 0;
    overflow-x: hidden;
  `,
  infos: css`
    display: flex;
    flex-direction: row;
    flex: 1 1;
    align-items: baseline;
    gap: ${theme.spacing(12)};
    ${ScreenMax.MD} {
      gap: ${theme.spacing(4)};
    }
  `,
  mobileContainer: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4),
  }),
  text: css({
    whiteSpace: 'nowrap',
  }),
  tag: css({}),
});
